import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientBuilderUserHydratedWithRoles } from "api-types/builderUsers/builderUsersResponses";
import type { ClientHomePlan, ClientHomePlanHydrated } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type {
  ClientLocation,
  ClientLocationHydrated,
  ClientLocationWithLevel,
} from "api-types/locations/locationsResponses";
import type { ClientLotCommunityPlan } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { ClientUser } from "api-types/users/usersResponses";
import type { Building } from "building-types/building/buildingTypes";
import { DEFAULT_STUDIO_DOC_NAME } from "building-types/doc/docDefaultConstants";
import { FALLBACK_VALUE } from "common/constants";
import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { formatDate, parseISO8601Format } from "common/utils/dates";
import { formatDollars } from "common/utils/formatDollars";
import { numMax, numSub } from "common/utils/nominalNumbers";
import { inches } from "doc/pdf/drawingLayout";
import { shouldDisplayPricing } from "lib/components/builders/communities/utils/pricingVisibility";
import type { PDFOption } from "lib/house/pdf/pdfOption";
import { getBuilderUserRoleName } from "lib/security/shared";
import { PDFTextMetricsProvider } from "pdf/pdfTextMetricsProvider";
import { brochureFontSizes, fonts } from "pdf/planFonts";
import type { BrochureContext } from "./BrochureContext";

// Used for rendering within the context of Studio, where some data is not available
const placeholders = {
  builder: "Placeholder Builder",
  brochureDescription:
    "Neque et dolores architecto sed minima quaerat porro. Dolores iste voluptas optio nihil quas distinctio earum tenetur distinctio vel et deleniti aspernatur qui. Maiores et pariatur et ipsum. Dolorum voluptas ut dolore qui pariatur provident minima amet provident eum eius nisi. Enim quo placeat numquam laboriosam architecto tempore. Aspernatur dignissimos quo occaecati quia quia.",
};

function getBuildingName(building: Pick<Building, "name">) {
  if (building.name.startsWith("Untitled")) {
    return DEFAULT_STUDIO_DOC_NAME;
  }

  //remove .home file uid extension
  return building.name.replace(/\s?.home(-\d+)?$/, "");
}

export const brochureValuePlaceholder = FALLBACK_VALUE;

const iso8601FormatRegex = /\d{8}T?\d{8,9}/;
function convertPlanNameDateToHumanReadable(planName: string): string {
  const [matched] = planName.match(iso8601FormatRegex) ?? [];
  if (matched) {
    const date = parseISO8601Format(matched);
    if (date) {
      return planName.replace(matched, formatDate(date));
    }
    // If we matched a timestamp pattern but couldn't parse it, strip it out entirely
    return planName.replace(/:\s*\d{8}T?\d{8,9}$/, "").trim();
  }
  return planName;
}

export function getBrochurePlanName({
  building,
  lotCommunityPlan,
  locationPlan,
  masterPlan,
}: {
  building: Pick<Building, "name">;
  lotCommunityPlan?: Pick<ClientLotCommunityPlan, "name">;
  locationPlan?: Pick<ClientLocationPlan, "name">;
  masterPlan?: Pick<ClientMasterPlan, "name">;
}): string {
  let planName = getBuildingName(building);

  if (lotCommunityPlan?.name) {
    planName = lotCommunityPlan.name;
  } else if (locationPlan?.name) {
    planName = locationPlan.name;
  } else if (masterPlan?.name) {
    planName = masterPlan.name;
  }

  return convertPlanNameDateToHumanReadable(planName);
}

export function getBrochureUserRole(
  user: ClientUser | null,
  homePlan?: ClientHomePlanHydrated | null,
  location?: Pick<ClientLocationHydrated, "salesStaff"> | null,
  builderUser?: ClientBuilderUserHydratedWithRoles,
): string {
  let brochureUserRole = brochureValuePlaceholder;

  if (homePlan?.salesStaff || location?.salesStaff || user?.tempUserRole === "sales_agent") {
    brochureUserRole = "Sales Agent";
  } else if (user) {
    brochureUserRole = getBuilderUserRoleName(user, builderUser) ?? "";
  }

  return brochureUserRole;
}

export function renderPrice(price?: number) {
  return price ? formatDollars(price) : brochureValuePlaceholder;
}

export function getCustomerName(homePlan?: ClientHomePlan, customer?: ClientUser) {
  const name = homePlan?.customerInfo?.name;

  if (name) {
    return name;
  } else if (customer) {
    return `${customer.firstName} ${customer.lastName}`;
  }
  return undefined;
}

export function getCustomerEmail(homePlan?: ClientHomePlan) {
  return homePlan?.customerInfo?.email;
}

interface FormatMasterPlanDescriptionParams {
  planName: string;
  options: PDFOption[];
  builder?: ClientBuilder;
  masterPlan?: ClientMasterPlan;
  homePlan?: ClientHomePlanHydrated;
  context: BrochureContext;
}

export function formatMasterPlanDescription({
  planName,
  options,
  builder,
  masterPlan,
  homePlan,
  context,
}: FormatMasterPlanDescriptionParams) {
  const homeName = planName ? `${planName} home` : "home";
  let optionsDescription: string = `Your ${homeName} includes a`;
  const optionsCopy = [...options];
  const sortedOptions = optionsCopy.sort((a, b) => {
    if (!a.isBaseOption === !b.isBaseOption) {
      if (a.price && b.price) {
        return b.price - a.price;
      } else {
        return a.price ? -1 : 1;
      }
    } else {
      return !a.isBaseOption ? -1 : 1;
    }
  });

  if (sortedOptions.length >= 3) {
    optionsDescription += ` ${sortedOptions[0].name}, ${sortedOptions[1].name}`;
    if (sortedOptions.length === 3) {
      optionsDescription += ` and ${sortedOptions[2].name} option`;
    } else {
      optionsDescription += `, ${sortedOptions[2].name} and ${sortedOptions.length - 3} other options`;
    }
  } else if (sortedOptions.length === 2) {
    optionsDescription += ` ${sortedOptions[0].name} and ${sortedOptions[1].name} option`;
  } else if (sortedOptions.length === 1) {
    optionsDescription += ` ${sortedOptions[0].name} option`;
  } else {
    optionsDescription = `You haven't selected any options for this plan`;
  }

  let brochureDescription = masterPlan?.description ?? "";

  if (["studio", "admin-preview"].includes(context)) {
    //limit brochure description placeholder to 300 chars to given accurate representation for vertical positioning
    (brochureDescription = placeholders.brochureDescription).slice(0, 300);
  }

  const parts = [`${optionsDescription}. ${brochureDescription}`];

  if (homePlan?.totalPriceOverride) {
    parts.push(builder?.salesConfig?.optionsDisplayConfig?.totalPriceOverrideDescription ?? "");
  }

  return parts.filter(Boolean).join(" ");
}

export function getBuildingNameFontSize(planName: string, boxWidth: SheetInches): SheetInches {
  // Pick the largest font tier whose rendered single-line width fits within boxWidth.
  const candidates: SheetInches[] = [
    brochureFontSizes.extraLarge,
    brochureFontSizes.largest,
    brochureFontSizes.larger,
    brochureFontSizes.large,
    brochureFontSizes.medLarge,
    brochureFontSizes.medium,
  ];

  for (const fontSize of candidates) {
    const width = PDFTextMetricsProvider.instance.widthOfString({
      text: planName,
      fontSize,
      font: fonts.brochureBold,
    });
    if (width <= boxWidth) {
      return fontSize;
    }
  }

  // Downstream layout measures the wrapped medium title and pushes the rows below it down.
  return brochureFontSizes.medium;
}

/**
 * Returns the vertical space beyond a single line that the plan-name title occupies after
 * wrapping. When the title wraps (which can happen at very long plan names or narrow column
 * widths), downstream header elements need to be pushed down by this amount to avoid overlap.
 */
export function getBuildingNameExtraHeight(
  planName: string,
  fontSize: SheetInches,
  boxWidth: SheetInches,
): SheetInches {
  const { height } = PDFTextMetricsProvider.instance.measureText({
    text: planName,
    fontSize,
    font: fonts.brochureBold,
    boxWidth,
  });
  const lineHeight = PDFTextMetricsProvider.instance.lineHeight(fontSize, fonts.brochureBold);
  return numMax(ZERO_INCHES, numSub(height, lineHeight));
}

export function getCustomerHeadLineY(fontSize: SheetInches): SheetInches {
  // extraLarge's line height (~0.526") needs more clearance so the title does not overlap
  // the customer headline when a lot address shifts the headline up by 0.125".
  if (fontSize === brochureFontSizes.extraLarge) {
    return inches(0.7);
  }
  return inches(0.57);
}

export function getTextAndImageTableY(fontSize: SheetInches): SheetInches {
  // extraLarge's taller headline (0.7") needs the stats table lower to stay clear of it.
  if (fontSize === brochureFontSizes.extraLarge) {
    return inches(0.94);
  }
  return inches(0.85);
}

/**
 * Determines if the brochure pricing should be displayed according to pricingVisibility.ts rules and whether the brochure is public or internal
 *
 * @param isPublic - Whether the brochure is being generated for a public user
 * @param location - The location of the brochure
 * @param ancestors - The ancestors of the location
 * @param builder - The builder of the brochure
 * @returns Whether the brochure pricing should be displayed
 */
export function shouldDisplayBrochurePricing(
  isPublic: boolean,
  location?: ClientLocation | null,
  ancestors?: ClientLocation[] | null,
  builder?: ClientBuilder,
): boolean {
  const salesBrochureToggle = shouldDisplayPricing("salesBrochure", location, ancestors, builder);

  if (!salesBrochureToggle) {
    return false;
  }

  const publicLinksPricingToggle = shouldDisplayPricing("publicHomePlanLinks", location, ancestors, builder);
  const internalLinksPricingToggle = shouldDisplayPricing("internalHomePlanLinks", location, ancestors, builder);

  if (publicLinksPricingToggle && internalLinksPricingToggle) {
    return true;
  }

  // Sales brochure acts as a master toggle, if both public and internal links pricing toggles are off, the brochure pricing should be displayed unless specified otherwise
  if (!publicLinksPricingToggle && !internalLinksPricingToggle) {
    return true;
  }

  return isPublic ? publicLinksPricingToggle : internalLinksPricingToggle;
}

/**
 * Searches a location and its ancestor chain for a phone number, traversing from the
 * location itself up through its ancestors in ascending hierarchy order (closest parent first,
 * determined by depth).
 * Returns the phone string (unformatted) or null if none found.
 */
export function findLocationPhone(
  location: ClientLocationWithLevel | undefined | null,
  ancestors: ClientLocationWithLevel[],
): string | null {
  if (location?.phone) {
    return location.phone;
  }
  const sortedAncestors = [...ancestors].sort((a, b) => b.depth - a.depth);
  return sortedAncestors.find((a) => a.phone)?.phone ?? null;
}
